import { c as createFocusTrap } from './focus-trap.esm-BUj_YYcQ.js';
import { pubsub } from '@services';

class DrawerElement extends HTMLElement {
  /** @type {HTMLElement[]} */ #controllers;
  /** @type {HTMLElement} */ #target;
  /** @type {HTMLElement} */ #drawerBody = this.querySelector('[data-drawer-body]');
  #subscribe = {
    eventName: this.dataset.subscribeEventName,
    eventAction: this.dataset.subscribeEventAction || 'open',
  };

  #trapFocus = createFocusTrap(this.#drawerBody, {
    allowOutsideClick: true,
    clickOutsideDeactivates: (event) => {
      // Don't let focus-trap handle clicks on controller elements - we handle those
      const controller = event.target.closest(`[aria-controls="${this.id}"]`);
      return !controller;
    },
    onActivate: () => this.#open(),
    onPostDeactivate: () => this.#close(),
  });

  connectedCallback() {
    this.#setControllersAndTarget();
    document.addEventListener('click', (event) => this.#handleDocumentClick(event));
    this.#subscribe.eventName &&
      pubsub.subscribe(this.#subscribe.eventName, () => {
        this.#toggleFocusTrap(this.#subscribe.eventAction === 'open');
      });
    this.#handleShopifyEvents();
  }

  #setControllersAndTarget() {
    this.#controllers = [...document.querySelectorAll(`[aria-controls="${this.id}"]`)];
    this.#target = this.#controllers[0].ariaControlsElements[0];
  }

  /**
   * Toggle the focus trap based on the boolean parameter
   * @param {boolean} boolean - True to activate the focus trap, false to deactivate it
   */
  #toggleFocusTrap(boolean) {
    boolean ? this.#trapFocus.activate() : this.#trapFocus.deactivate();
  }

  /**
   * Handle document click events
   * @param {Event} event - The event object
   */
  #handleDocumentClick(event) {
    // Skip if drawer is locked by theme editor
    if (this.lockedByThemeEditor) return;

    // Check if click was on a controller for this drawer
    const controller = event.target.closest(`[aria-controls="${this.id}"]`);
    if (!controller) return;

    // Prevent navigation for anchor controllers
    event.preventDefault();

    // Refresh references in case DOM changed
    this.#setControllersAndTarget();

    // Toggle focus trap based on drawer visibility
    this.#toggleFocusTrap(this.#target.ariaHidden === 'true');
  }

  #open() {
    this.#controllers.forEach((controller) => (controller.ariaExpanded = 'true'));
    this.#target.ariaHidden = 'false';
    this.#target.removeAttribute('tabindex');
    pubsub.publish('drawer:open', { id: this.id });
  }

  #close() {
    // Blur any focused element inside the drawer before hiding it
    // This prevents the aria-hidden accessibility violation
    if (this.contains(document.activeElement)) {
      document.activeElement.blur();
    }

    this.#controllers.forEach((controller) => (controller.ariaExpanded = 'false'));
    this.#target.ariaHidden = 'true';
    this.#target.setAttribute('tabindex', -1);
    pubsub.publish('drawer:close', { id: this.id });
  }

  /**
   * Handle block select events
   */
  #handleShopifyEvents() {
    const events = [
      'shopify:section:load',
      'shopify:section:select',
      'shopify:section:unload',
      'shopify:section:deselect',
    ];

    events.forEach((eventName) => {
      document.addEventListener(eventName, (event) => {
        const {
          detail: { sectionId },
          type,
        } = event;

        // Only handle events for this drawer
        if (sectionId !== this.id) return;

        switch (type) {
          case 'shopify:section:load':
          case 'shopify:section:select':
            this.lockedByThemeEditor = true;
            this.#open();
            break;
          case 'shopify:section:unload':
          case 'shopify:section:deselect':
            this.lockedByThemeEditor = false;
            this.#close();
            break;
        }
      });
    });
  }
}

customElements.define('drawer-element', DrawerElement);

export { DrawerElement };
